const LIFE_STORY_PUBLIC_PATH_PATTERNS = [
  /^\/life-story\/[^/]+\/?$/i,
  /^\/public\/life-story\/[^/]+\/?$/i,
  /^\/life-review\/invite\/[^/]+\/?$/i,
  /^\/life-review\/share\/[^/]+\/?$/i
];

const LIFE_STORY_QUERY_TOKEN_PATH_PATTERNS = [
  /^\/life-story\/?$/i,
  /^\/public\/life-story\/?$/i,
  /^\/life-review\/invite\/?$/i,
  /^\/life-review\/share\/?$/i
];

const LIFE_STORY_PUBLIC_HASH_PATTERNS = [
  { pattern: /^#\/?life-story\/([^/?#]+)\/?$/i, routePrefix: '/life-story/' },
  { pattern: /^#\/?public\/life-story\/([^/?#]+)\/?$/i, routePrefix: '/public/life-story/' },
  { pattern: /^#\/?life-review\/invite\/([^/?#]+)\/?$/i, routePrefix: '/life-review/invite/' },
  { pattern: /^#\/?life-review\/share\/([^/?#]+)\/?$/i, routePrefix: '/life-review/share/' }
];

const ARTIFACT_PUBLIC_PATH_PATTERNS = [/^\/artifacts\/[^/]+\/?$/i, /^\/public\/artifacts\/[^/]+\/?$/i];

const ARTIFACT_QUERY_TOKEN_PATH_PATTERNS = [/^\/artifacts\/?$/i, /^\/public\/artifacts\/?$/i];

const ARTIFACT_PUBLIC_HASH_PATTERNS = [
  { pattern: /^#\/?artifacts\/([^/?#]+)\/?$/i, routePrefix: '/artifacts/' },
  { pattern: /^#\/?public\/artifacts\/([^/?#]+)\/?$/i, routePrefix: '/public/artifacts/' }
];

const QUERY_TOKEN_KEYS = ['token', 'lifeStoryToken', 'inviteToken', 'shareToken'];
const ARTIFACT_QUERY_TOKEN_KEYS = ['token', 'artifactToken', 'shareToken'];

const decodeToken = (rawToken: string) => {
  try {
    return decodeURIComponent(rawToken);
  } catch {
    return rawToken;
  }
};

export const isLifeStoryPublicPath = (pathname: string): boolean =>
  LIFE_STORY_PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(pathname));

export const isLifeStoryPublicHash = (hash: string): boolean =>
  LIFE_STORY_PUBLIC_HASH_PATTERNS.some(({ pattern }) => pattern.test(hash));

export const isLifeStoryPublicQuery = ({ pathname, search }: { pathname: string; search?: string }): boolean =>
  Boolean(getTokenFromSearch(search)) && LIFE_STORY_QUERY_TOKEN_PATH_PATTERNS.some((pattern) => pattern.test(pathname));

export const isLifeStoryPublicContext = ({ pathname, hash, search }: { pathname: string; hash: string; search?: string }): boolean =>
  isLifeStoryPublicPath(pathname) || isLifeStoryPublicHash(hash) || isLifeStoryPublicQuery({ pathname, search });

export const isArtifactPublicPath = (pathname: string): boolean =>
  ARTIFACT_PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(pathname));

export const isArtifactPublicHash = (hash: string): boolean =>
  ARTIFACT_PUBLIC_HASH_PATTERNS.some(({ pattern }) => pattern.test(hash));

export const getArtifactTokenFromSearch = (search?: string): string => {
  if (!search) {
    return '';
  }

  const params = new URLSearchParams(search);
  for (const key of ARTIFACT_QUERY_TOKEN_KEYS) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }

  return '';
};

export const isArtifactPublicQuery = ({ pathname, search }: { pathname: string; search?: string }): boolean =>
  Boolean(getArtifactTokenFromSearch(search)) && ARTIFACT_QUERY_TOKEN_PATH_PATTERNS.some((pattern) => pattern.test(pathname));

export const isArtifactPublicContext = ({ pathname, hash, search }: { pathname: string; hash: string; search?: string }): boolean =>
  isArtifactPublicPath(pathname) || isArtifactPublicHash(hash) || isArtifactPublicQuery({ pathname, search });

export const getTokenFromHash = (hash?: string): string => {
  if (!hash) {
    return '';
  }

  for (const { pattern } of LIFE_STORY_PUBLIC_HASH_PATTERNS) {
    const match = hash.match(pattern);
    if (match?.[1]) {
      return decodeToken(match[1]);
    }
  }

  return '';
};

export const getTokenFromSearch = (search?: string): string => {
  if (!search) {
    return '';
  }

  const params = new URLSearchParams(search);
  for (const key of QUERY_TOKEN_KEYS) {
    const value = params.get(key);
    if (value) {
      return value;
    }
  }

  return '';
};

export const getArtifactTokenFromHash = (hash?: string): string => {
  if (!hash) {
    return '';
  }

  for (const { pattern } of ARTIFACT_PUBLIC_HASH_PATTERNS) {
    const match = hash.match(pattern);
    if (match?.[1]) {
      return decodeToken(match[1]);
    }
  }

  return '';
};

export const getCanonicalLifeStoryPathFromHash = (hash?: string): string | null => {
  if (!hash) {
    return null;
  }

  for (const { pattern, routePrefix } of LIFE_STORY_PUBLIC_HASH_PATTERNS) {
    const match = hash.match(pattern);
    if (match?.[1]) {
      return `${routePrefix}${encodeURIComponent(decodeToken(match[1]))}`;
    }
  }

  return null;
};

export const getCanonicalLifeStoryPathFromQuery = (pathname: string, search?: string): string | null => {
  const queryToken = getTokenFromSearch(search);
  if (!queryToken) {
    return null;
  }

  if (/^\/life-story\/?$/i.test(pathname)) {
    return `/life-story/${encodeURIComponent(queryToken)}`;
  }

  if (/^\/public\/life-story\/?$/i.test(pathname)) {
    return `/public/life-story/${encodeURIComponent(queryToken)}`;
  }

  if (/^\/life-review\/invite\/?$/i.test(pathname)) {
    return `/life-review/invite/${encodeURIComponent(queryToken)}`;
  }

  if (/^\/life-review\/share\/?$/i.test(pathname)) {
    return `/life-review/share/${encodeURIComponent(queryToken)}`;
  }

  return null;
};

export const getCanonicalArtifactPathFromHash = (hash?: string): string | null => {
  if (!hash) {
    return null;
  }

  for (const { pattern, routePrefix } of ARTIFACT_PUBLIC_HASH_PATTERNS) {
    const match = hash.match(pattern);
    if (match?.[1]) {
      return `${routePrefix}${encodeURIComponent(decodeToken(match[1]))}`;
    }
  }

  return null;
};

export const getCanonicalArtifactPathFromQuery = (pathname: string, search?: string): string | null => {
  const queryToken = getArtifactTokenFromSearch(search);
  if (!queryToken) {
    return null;
  }

  if (/^\/artifacts\/?$/i.test(pathname)) {
    return `/artifacts/${encodeURIComponent(queryToken)}`;
  }

  if (/^\/public\/artifacts\/?$/i.test(pathname)) {
    return `/public/artifacts/${encodeURIComponent(queryToken)}`;
  }

  return null;
};
